import { ENodeVehicle } from '@vae/server/src/interfaces/eNode/eNodeVehicle.interface'
import { SynerallInvoice } from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import { ProductConsumptionCosts } from '@vae/server/src/modules/ExternalAPIs/Synerall/utils/costCalculators'
import { ModalOption } from '@vae/server/src/types/notifications'
import React, { createContext, useState } from 'react'
import { logEvent } from '../analytics/utils/firebaseUtils'
import { TierData } from '../modals/badges/types/tierData'

export type ModalVariableTypes = {
	'badges-tiers': { tiers: TierData[]; categoryName: string }
	'badges-detail': TierData
	previousCharges: { eNodeId: string }
	solarPanelDetail: string
	invoiceDetail: SynerallInvoice
}

export type GetModalVariableType<T extends ModalOption> = T extends keyof ModalVariableTypes
	? ModalVariableTypes[T]
	: undefined

type ModalState = {
	[K in ModalOption]: {
		activeModal: K
		modalVariable?: GetModalVariableType<K>
	}
}[ModalOption]

export interface MenuContextType {
	modalState: ModalState[]
	currentModal?: ModalState
	modalOnTop?: ModalOption
	insertModal: <K extends ModalOption>(newModal: K, modalVariable?: GetModalVariableType<K>) => void
	closeModal: () => void
	modalStackLength: number
	activeReportId: number
	setActiveReportId: React.Dispatch<React.SetStateAction<number>>
	consumptionElementToAdd?: any
	setConsumptionElementToAdd: React.Dispatch<React.SetStateAction<any | undefined>>
	clearModals: () => void
	setActiveOffer: React.Dispatch<React.SetStateAction<ProductConsumptionCosts | undefined>>
	activeOffer: ProductConsumptionCosts | undefined
	timeValue: string
	setTimeValue: React.Dispatch<React.SetStateAction<string>>
	selectedVehicles: ENodeVehicle[]
	setSelectedVehicles: React.Dispatch<React.SetStateAction<ENodeVehicle[]>>
}

export const ModalContext = createContext({} as MenuContextType)
const ModalContextProvider = (props: any) => {
	const [modalState, setModalState] = useState<ModalState[]>([])
	const [activeOffer, setActiveOffer] = useState<ProductConsumptionCosts>()
	const [activeReportId, setActiveReportId] = useState<number>(0)
	const [consumptionElementToAdd, setConsumptionElementToAdd] = useState<any>()
	const [timeValue, setTimeValue] = useState<string>(new Date().toISOString())
	const [selectedVehicles, setSelectedVehicles] = useState<ENodeVehicle[]>([])

	function insertModal<K extends ModalOption>(
		newModal: K,
		modalVariable?: GetModalVariableType<K>
	) {
		return setModalState(state => [
			...state,
			{ activeModal: newModal, modalVariable } as ModalState,
		])
	}

	const clearModals = () => {
		logEvent('navigation-close-modals')
		setModalState([])
	}

	const closeModal = () => {
		setModalState(state => {
			state.splice(-1, 1)
			return [...state]
		})
	}

	const currentModal = modalState[modalState.length - 1]
	const modalOnTop = currentModal?.activeModal

	return (
		<ModalContext.Provider
			value={{
				modalState,
				currentModal,
				modalOnTop,
				insertModal,
				closeModal,
				modalStackLength: modalState.length,
				activeReportId,
				setActiveReportId,
				consumptionElementToAdd,
				setConsumptionElementToAdd,
				clearModals,
				setActiveOffer,
				activeOffer,
				timeValue,
				setTimeValue,
				selectedVehicles,
				setSelectedVehicles,
			}}
		>
			{props.children}
		</ModalContext.Provider>
	)
}

export default ModalContextProvider
